import { useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { signalRConsoleLogger } from '../logging/signalRConsoleLogger';
import {
  CanvasItemMove,
  CanvasItemsBroughtToFront,
  CanvasItemsDeleted,
  CanvasItemsMoveCommitted,
  CanvasItemsMovedDelta,
  CanvasSnapshot,
  ItemLock,
  ItemLocksChanged,
  StickyState,
  TextLabelState,
  TryAcquireItemLocksResult,
} from '../api/Requests';

/** Live drag relay — at most one batched ItemsMoved per this interval. */
export const LIVE_MOVE_THROTTLE_MS = 33;

/** Cursor relay — at most one CursorMoved per this interval, latest point only. */
export const CURSOR_THROTTLE_MS = 100;

/** Skip a cursor flush when board-space movement since the last send is below this. */
export const CURSOR_MIN_DELTA_PX = 2;

/**
 * elmah.io titles a client message after the value passed to console.error, so a bare
 * `console.error(err)` on a rejected hub call arrives as an untriageable message called
 * "Error" with only minified frames. Naming the hub operation makes the log actionable.
 */
function logHubFailure(operation: string, err: unknown): void {
  console.error('RetroHub ' + operation + ' failed:', err);
}

export interface CursorPosition {
  playerId: string;
  playerName: string;
  x: number;
  y: number;
}

export interface UseRetroSyncOptions {
  roomUrlId: string;
  playerId: string;
  playerName: string;
  onStickyChanged: (diff: StickyState) => void;
  onStickyDeleted: (stickyId: string) => void;
  onTextLabelChanged: (diff: TextLabelState) => void;
  onTextLabelDeleted: (labelId: string) => void;
  onItemsMoved?: (items: CanvasItemMove[]) => void;
  onItemsMovedDelta?: (payload: CanvasItemsMovedDelta) => void;
  onItemsMoveCommitted?: (payload: CanvasItemsMoveCommitted) => void;
  onItemsCommitted?: (snapshot: CanvasSnapshot) => void;
  onItemsBroughtToFront?: (payload: CanvasItemsBroughtToFront) => void;
  onItemsDeleted?: (payload: CanvasItemsDeleted) => void;
  onCursorMoved: (cursor: CursorPosition) => void;
  onCursorLeft?: (playerId: string) => void;
  onSnapshot: (snapshot: CanvasSnapshot) => void;
  onTemplateChanged?: (template: string | null) => void;
  onRetroEnded?: () => void;
  onRetroReopened?: () => void;
  /** Called when a persist hub invoke fails or the connection is down. */
  onPersistError?: () => void;
  onItemLocksSnapshot?: (locks: ItemLock[]) => void;
  onItemLocksChanged?: (changed: ItemLocksChanged) => void;
}

export function useRetroSync({
  roomUrlId,
  playerId,
  playerName,
  onStickyChanged,
  onStickyDeleted,
  onTextLabelChanged,
  onTextLabelDeleted,
  onItemsMoved,
  onItemsMovedDelta,
  onItemsMoveCommitted,
  onItemsCommitted,
  onItemsBroughtToFront,
  onItemsDeleted,
  onCursorMoved,
  onCursorLeft,
  onSnapshot,
  onTemplateChanged,
  onRetroEnded,
  onRetroReopened,
  onPersistError,
  onItemLocksSnapshot,
  onItemLocksChanged,
}: UseRetroSyncOptions) {
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const lastCursorSentAt = useRef<number>(0);
  const lastCursorSentPos = useRef<{ x: number; y: number } | null>(null);
  const pendingCursorRef = useRef<{ x: number; y: number } | null>(null);
  const cursorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingMovesRef = useRef<Map<string, CanvasItemMove>>(new Map());
  const lastMovesSentAt = useRef<number>(0);
  const movesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Compact multi-item move relay: one shared (dx, dy) pending per gesture instead of a
  // per-id map, since every listed id moves by the same amount. `sentMoveDeltaRef` tracks
  // the cumulative offset already sent so each flush can convert the caller's
  // since-gesture-start total into the increment-since-last-send the wire protocol expects.
  const pendingMoveDeltaRef = useRef<{ stickyIds: string[]; labelIds: string[]; dx: number; dy: number } | null>(null);
  const sentMoveDeltaRef = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const lastMoveDeltaSentAt = useRef<number>(0);
  const moveDeltaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True only while this connection has successfully (re)joined the room's SignalR
  // group and is in the server's authorized-connections map. Gates outbound board
  // traffic so a mid-drag reconnect (new ConnectionId) doesn't spam rejected invokes.
  const joinedRef = useRef(false);

  // Store each handler in a ref so the SignalR connection always calls the
  // latest version without needing to be re-established when a callback changes.
  const onStickyChangedRef    = useRef(onStickyChanged);    onStickyChangedRef.current    = onStickyChanged;
  const onStickyDeletedRef    = useRef(onStickyDeleted);    onStickyDeletedRef.current    = onStickyDeleted;
  const onTextLabelChangedRef = useRef(onTextLabelChanged); onTextLabelChangedRef.current = onTextLabelChanged;
  const onTextLabelDeletedRef = useRef(onTextLabelDeleted); onTextLabelDeletedRef.current = onTextLabelDeleted;
  const onItemsMovedRef       = useRef(onItemsMoved);       onItemsMovedRef.current       = onItemsMoved;
  const onItemsMovedDeltaRef  = useRef(onItemsMovedDelta);  onItemsMovedDeltaRef.current  = onItemsMovedDelta;
  const onItemsMoveCommittedRef = useRef(onItemsMoveCommitted); onItemsMoveCommittedRef.current = onItemsMoveCommitted;
  const onItemsCommittedRef   = useRef(onItemsCommitted);   onItemsCommittedRef.current   = onItemsCommitted;
  const onItemsBroughtToFrontRef = useRef(onItemsBroughtToFront); onItemsBroughtToFrontRef.current = onItemsBroughtToFront;
  const onItemsDeletedRef     = useRef(onItemsDeleted);     onItemsDeletedRef.current     = onItemsDeleted;
  const onCursorMovedRef      = useRef(onCursorMoved);      onCursorMovedRef.current      = onCursorMoved;
  const onCursorLeftRef       = useRef(onCursorLeft);       onCursorLeftRef.current       = onCursorLeft;
  const onSnapshotRef         = useRef(onSnapshot);         onSnapshotRef.current         = onSnapshot;
  const onTemplateChangedRef  = useRef(onTemplateChanged);  onTemplateChangedRef.current  = onTemplateChanged;
  const onRetroEndedRef       = useRef(onRetroEnded);       onRetroEndedRef.current       = onRetroEnded;
  const onRetroReopenedRef    = useRef(onRetroReopened);    onRetroReopenedRef.current     = onRetroReopened;
  const onPersistErrorRef     = useRef(onPersistError);     onPersistErrorRef.current     = onPersistError;
  const onItemLocksSnapshotRef = useRef(onItemLocksSnapshot); onItemLocksSnapshotRef.current = onItemLocksSnapshot;
  const onItemLocksChangedRef = useRef(onItemLocksChanged); onItemLocksChangedRef.current = onItemLocksChanged;

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl('/retrohub')
      .withAutomaticReconnect()
      .configureLogging(signalRConsoleLogger)
      .build();

    connectionRef.current = connection;

    connection.on('canvasSnapshot', (snapshot: CanvasSnapshot) => {
      onSnapshotRef.current(snapshot);
    });

    connection.on('stickyChanged', (diff: StickyState) => {
      onStickyChangedRef.current(diff);
    });

    connection.on('stickyDeleted', (stickyId: string) => {
      onStickyDeletedRef.current(stickyId);
    });

    connection.on('textLabelChanged', (diff: TextLabelState) => {
      onTextLabelChangedRef.current(diff);
    });

    connection.on('textLabelDeleted', (labelId: string) => {
      onTextLabelDeletedRef.current(labelId);
    });

    connection.on('itemsMoved', (items: CanvasItemMove[]) => {
      onItemsMovedRef.current?.(items);
    });

    connection.on('itemsMovedDelta', (payload: CanvasItemsMovedDelta) => {
      onItemsMovedDeltaRef.current?.(payload);
    });

    connection.on('itemsMoveCommitted', (payload: CanvasItemsMoveCommitted) => {
      onItemsMoveCommittedRef.current?.(payload);
    });

    connection.on('itemsCommitted', (snapshot: CanvasSnapshot) => {
      onItemsCommittedRef.current?.(snapshot);
    });

    connection.on('itemsBroughtToFront', (payload: CanvasItemsBroughtToFront) => {
      onItemsBroughtToFrontRef.current?.(payload);
    });

    connection.on('itemsDeleted', (payload: CanvasItemsDeleted) => {
      onItemsDeletedRef.current?.(payload);
    });

    connection.on('cursorMoved', (cursor: CursorPosition) => {
      onCursorMovedRef.current(cursor);
    });

    connection.on('cursorLeft', (playerId: string) => {
      onCursorLeftRef.current?.(playerId);
    });

    connection.on('templateChanged', (payload: { template?: string | null }) => {
      onTemplateChangedRef.current?.(payload.template ?? null);
    });

    connection.on('retroEnded', () => {
      onRetroEndedRef.current?.();
    });

    connection.on('retroReopened', () => {
      onRetroReopenedRef.current?.();
    });

    connection.on('itemLocksSnapshot', (locks: ItemLock[]) => {
      onItemLocksSnapshotRef.current?.(locks ?? []);
    });

    connection.on('itemLocksChanged', (payload: ItemLocksChanged) => {
      onItemLocksChangedRef.current?.(payload ?? { acquired: [], released: [] });
    });

    // SignalR's automatic reconnect gets a brand-new ConnectionId, which is neither
    // in the hub's authorized-connections map nor re-added to the room's group. Without
    // re-joining, every outbound invoke is rejected as unauthorized and every inbound
    // broadcast from other players is silently dropped, so the boards diverge.
    // Reconnect/timeout/1006 are expected (laptop sleep, frozen tab, network blip) —
    // warn only so elmah.io's console.error capture does not treat them as incidents.
    connection.onreconnecting((error?: Error) => {
      joinedRef.current = false;
      console.warn('RetroHub reconnecting with error: ' + (error?.message ?? 'connection lost'));
    });

    connection.onreconnected(() => {
      connection
        .invoke('JoinRetroRoom', playerId, playerName, roomUrlId)
        .then(() => {
          joinedRef.current = true;
        })
        .catch((err: Error) => {
          logHubFailure('rejoin', err);
          joinedRef.current = false;
        });
    });

    connection.onclose((error?: Error) => {
      joinedRef.current = false;
      if (error) {
        console.warn('RetroHub closed with error: ' + error.message);
      }
    });

    connection
      .start()
      .then(() => {
        return connection.invoke('JoinRetroRoom', playerId, playerName, roomUrlId);
      })
      .then(() => {
        joinedRef.current = true;
      })
      .catch((err: Error) => {
        logHubFailure('connection', err);
      });

    return () => {
      joinedRef.current = false;
      if (movesTimerRef.current) {
        clearTimeout(movesTimerRef.current);
        movesTimerRef.current = null;
      }
      if (moveDeltaTimerRef.current) {
        clearTimeout(moveDeltaTimerRef.current);
        moveDeltaTimerRef.current = null;
      }
      if (cursorTimerRef.current) {
        clearTimeout(cursorTimerRef.current);
        cursorTimerRef.current = null;
      }
      pendingMovesRef.current.clear();
      pendingMoveDeltaRef.current = null;
      pendingCursorRef.current = null;
      if (connection.state !== signalR.HubConnectionState.Disconnected) {
        connection.invoke('LeaveRetroRoom', playerId, playerName, roomUrlId).catch(() => null);
        connection.stop();
      }
    };
  }, [roomUrlId, playerId, playerName]);

  const invokePersist = useCallback((
    method: string,
    ...args: Array<string | number | string[] | StickyState[] | TextLabelState[]>
  ): Promise<boolean> => {
    const conn = connectionRef.current;
    if (conn?.state !== signalR.HubConnectionState.Connected || !joinedRef.current) {
      onPersistErrorRef.current?.();
      return Promise.resolve(false);
    }
    return conn.invoke(method, ...args).then(() => true).catch((err: Error) => {
      logHubFailure(method, err);
      onPersistErrorRef.current?.();
      return false;
    });
  }, []);

  const sendPendingMoves = useCallback(() => {
    if (movesTimerRef.current) {
      clearTimeout(movesTimerRef.current);
      movesTimerRef.current = null;
    }
    const items = Array.from(pendingMovesRef.current.values());
    if (items.length === 0) return;
    const conn = connectionRef.current;
    if (conn?.state !== signalR.HubConnectionState.Connected || !joinedRef.current) return;

    pendingMovesRef.current.clear();
    lastMovesSentAt.current = Date.now();
    conn.send('ItemsMoved', roomUrlId, items).catch((err) => logHubFailure('ItemsMoved', err));
  }, [roomUrlId]);

  const broadcastItemsMoved = useCallback((items: CanvasItemMove[]) => {
    if (!items?.length) return;
    for (const item of items) pendingMovesRef.current.set(item.id, item);

    const elapsed = Date.now() - lastMovesSentAt.current;
    if (elapsed >= LIVE_MOVE_THROTTLE_MS) {
      sendPendingMoves();
      return;
    }
    if (!movesTimerRef.current) {
      movesTimerRef.current = setTimeout(sendPendingMoves, LIVE_MOVE_THROTTLE_MS - elapsed);
    }
  }, [sendPendingMoves]);

  /** Flush coalesced live moves immediately — call on pointerup before the persist commit. */
  const flushLiveMoves = useCallback(() => {
    sendPendingMoves();
  }, [sendPendingMoves]);

  const sendPendingMoveDelta = useCallback(() => {
    if (moveDeltaTimerRef.current) {
      clearTimeout(moveDeltaTimerRef.current);
      moveDeltaTimerRef.current = null;
    }
    const pending = pendingMoveDeltaRef.current;
    if (!pending) return;
    const conn = connectionRef.current;
    if (conn?.state !== signalR.HubConnectionState.Connected || !joinedRef.current) return;

    pendingMoveDeltaRef.current = null;
    lastMoveDeltaSentAt.current = Date.now();

    const frameDx = pending.dx - sentMoveDeltaRef.current.dx;
    const frameDy = pending.dy - sentMoveDeltaRef.current.dy;
    sentMoveDeltaRef.current = { dx: pending.dx, dy: pending.dy };
    if (frameDx === 0 && frameDy === 0) return;

    conn.send('ItemsMovedDelta', roomUrlId, pending.stickyIds, pending.labelIds, frameDx, frameDy)
      .catch((err) => logHubFailure('ItemsMovedDelta', err));
  }, [roomUrlId]);

  /**
   * Relay a multi-item drag's *cumulative* offset since the gesture started — the same
   * shape CanvasSurface already tracks for local rendering, so callers never need to
   * compute a per-frame delta themselves. Internally coalesced and converted to the
   * increment since the last relay send before going out, so a receiver can apply it
   * directly (`item.x += dx`) without knowing where the gesture started.
   */
  const broadcastItemsMovedDelta = useCallback((stickyIds: string[], labelIds: string[], dx: number, dy: number) => {
    if (stickyIds.length === 0 && labelIds.length === 0) return;
    pendingMoveDeltaRef.current = { stickyIds, labelIds, dx, dy };

    const elapsed = Date.now() - lastMoveDeltaSentAt.current;
    if (elapsed >= LIVE_MOVE_THROTTLE_MS) {
      sendPendingMoveDelta();
      return;
    }
    if (!moveDeltaTimerRef.current) {
      moveDeltaTimerRef.current = setTimeout(sendPendingMoveDelta, LIVE_MOVE_THROTTLE_MS - elapsed);
    }
  }, [sendPendingMoveDelta]);

  /** Flush a pending relay delta immediately — call on drop, before the persist commit. */
  const flushItemsMovedDelta = useCallback(() => {
    sendPendingMoveDelta();
  }, [sendPendingMoveDelta]);

  /** Reset gesture tracking to zero — call when a new multi-item drag starts. */
  const resetItemsMovedDelta = useCallback(() => {
    if (moveDeltaTimerRef.current) {
      clearTimeout(moveDeltaTimerRef.current);
      moveDeltaTimerRef.current = null;
    }
    pendingMoveDeltaRef.current = null;
    sentMoveDeltaRef.current = { dx: 0, dy: 0 };
  }, []);

  const broadcastItemsCommitted = useCallback((stickies: StickyState[], labels: TextLabelState[]): Promise<boolean> => {
    if ((!stickies || stickies.length === 0) && (!labels || labels.length === 0)) {
      return Promise.resolve(true);
    }
    return invokePersist('ItemsCommitted', roomUrlId, stickies ?? [], labels ?? []);
  }, [invokePersist, roomUrlId]);

  /** Persists a multi-item drag's total displacement. dx/dy are the gesture total, not a per-frame increment. */
  const broadcastItemsMoveCommitted = useCallback((
    stickyIds: string[],
    labelIds: string[],
    dx: number,
    dy: number,
    updatedAt: string,
  ): Promise<boolean> => {
    if (stickyIds.length === 0 && labelIds.length === 0) return Promise.resolve(true);
    return invokePersist('ItemsMoveCommitted', roomUrlId, stickyIds, labelIds, dx, dy, updatedAt);
  }, [invokePersist, roomUrlId]);

  /** stickyIds/labelIds must be ordered by current zIndex ascending within each kind — see computeBringToFrontOrder. */
  const broadcastItemsBroughtToFront = useCallback((
    stickyIds: string[],
    labelIds: string[],
    baseZ: number,
    updatedAt: string,
  ): Promise<boolean> => {
    if (stickyIds.length === 0 && labelIds.length === 0) return Promise.resolve(true);
    return invokePersist('ItemsBroughtToFront', roomUrlId, stickyIds, labelIds, baseZ, updatedAt);
  }, [invokePersist, roomUrlId]);

  const broadcastItemsDeleted = useCallback((stickyIds: string[], labelIds: string[]): Promise<boolean> => {
    if ((!stickyIds || stickyIds.length === 0) && (!labelIds || labelIds.length === 0)) {
      return Promise.resolve(true);
    }
    return invokePersist('ItemsDeleted', roomUrlId, stickyIds ?? [], labelIds ?? []);
  }, [invokePersist, roomUrlId]);

  const sendPendingCursor = useCallback(() => {
    if (cursorTimerRef.current) {
      clearTimeout(cursorTimerRef.current);
      cursorTimerRef.current = null;
    }
    const pending = pendingCursorRef.current;
    if (!pending) return;
    pendingCursorRef.current = null;
    lastCursorSentAt.current = Date.now();

    const last = lastCursorSentPos.current;
    if (last) {
      const dx = pending.x - last.x;
      const dy = pending.y - last.y;
      if (dx * dx + dy * dy < CURSOR_MIN_DELTA_PX * CURSOR_MIN_DELTA_PX) return;
    }

    const conn = connectionRef.current;
    if (conn?.state !== signalR.HubConnectionState.Connected) return;

    lastCursorSentPos.current = pending;
    conn.send('CursorMoved', roomUrlId, playerId, playerName, pending.x, pending.y)
      .catch((err) => logHubFailure('CursorMoved', err));
  }, [roomUrlId, playerId, playerName]);

  // Coalesced to 100ms, latest point only. Fire-and-forget — never persisted.
  const broadcastCursor = useCallback((x: number, y: number) => {
    pendingCursorRef.current = { x, y };

    const elapsed = Date.now() - lastCursorSentAt.current;
    if (elapsed >= CURSOR_THROTTLE_MS) {
      sendPendingCursor();
      return;
    }
    if (!cursorTimerRef.current) {
      cursorTimerRef.current = setTimeout(sendPendingCursor, CURSOR_THROTTLE_MS - elapsed);
    }
  }, [sendPendingCursor]);

  const broadcastCursorLeft = useCallback(() => {
    pendingCursorRef.current = null;
    if (cursorTimerRef.current) {
      clearTimeout(cursorTimerRef.current);
      cursorTimerRef.current = null;
    }
    lastCursorSentPos.current = null;
    lastCursorSentAt.current = 0;

    const conn = connectionRef.current;
    if (conn?.state === signalR.HubConnectionState.Connected) {
      conn.send('CursorLeft', roomUrlId, playerId).catch((err) => logHubFailure('CursorLeft', err));
    }
  }, [roomUrlId, playerId]);

  /**
   * Exclusive occupancy for a mutating gesture. When the hub is down, grants locally
   * so a single disconnected player can still edit.
   */
  const tryAcquireItemLocks = useCallback(async (
    stickyIds: string[],
    labelIds: string[],
  ): Promise<TryAcquireItemLocksResult> => {
    if (stickyIds.length === 0 && labelIds.length === 0) {
      return { granted: true, locks: [] };
    }
    const conn = connectionRef.current;
    if (conn?.state !== signalR.HubConnectionState.Connected || !joinedRef.current) {
      return { granted: true, locks: [] };
    }
    try {
      const result = await conn.invoke<TryAcquireItemLocksResult>(
        'TryAcquireItemLocks',
        roomUrlId,
        stickyIds,
        labelIds,
      );
      return result ?? { granted: false, locks: [] };
    } catch (err) {
      logHubFailure('TryAcquireItemLocks', err);
      return { granted: false, locks: [] };
    }
  }, [roomUrlId]);

  const releaseItemLocks = useCallback((stickyIds: string[], labelIds: string[]): void => {
    if (stickyIds.length === 0 && labelIds.length === 0) return;
    const conn = connectionRef.current;
    if (conn?.state !== signalR.HubConnectionState.Connected || !joinedRef.current) return;
    conn.invoke('ReleaseItemLocks', roomUrlId, stickyIds, labelIds)
      .catch((err) => logHubFailure('ReleaseItemLocks', err));
  }, [roomUrlId]);

  return {
    broadcastItemsMoved,
    flushLiveMoves,
    broadcastItemsMovedDelta,
    flushItemsMovedDelta,
    resetItemsMovedDelta,
    broadcastItemsCommitted,
    broadcastItemsMoveCommitted,
    broadcastItemsBroughtToFront,
    broadcastItemsDeleted,
    broadcastCursor,
    broadcastCursorLeft,
    tryAcquireItemLocks,
    releaseItemLocks,
  };
}
